import { useEffect, useState } from 'react';

import { store } from 'redux/store';
import { getTradingOffers } from 'modules/trading/redux/tradingOfferActions';
import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import { getOfferStatus } from 'modules/trading/utils/getOfferStatus';

const getShareClassId = (shareClass: TradingOffer['shareClass']) => {
  if (!shareClass) return '';
  return typeof shareClass === 'string' ? shareClass : shareClass._id || '';
};

const getPartnerIdFromOffer = (partner: TradingOffer['partner']) => {
  if (!partner) return '';
  return typeof partner === 'string' ? partner : partner._id || '';
};

type Params = {
  societyId?: string;
  partnerId: string;
  excludeTradingOfferId?: string;
  tradingOfferType: TradingOfferType;
};

const emptyMap: Record<string, number> = {};

/**
 * For each share class, sums `sharesCount.total` of active sell offers of the partner (excludes the offer being edited).
 * It is used for the selector labels and the maximum transferable by class.
 */
export const useGetReservedShares = ({
  societyId,
  partnerId,
  excludeTradingOfferId,
  tradingOfferType,
}: Params) => {
  const [reservedSharesByShareClass, setReservedSharesByShareClass] =
    useState<Record<string, number>>(emptyMap);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      !societyId ||
      !partnerId ||
      tradingOfferType !== TradingOfferType.SELL
    ) {
      setReservedSharesByShareClass(emptyMap);
      setIsLoading(false);
      return;
    }

    let active = true;
    setIsLoading(true);

    const run = async () => {
      try {
        const result = await store.dispatch(
          getTradingOffers({
            societyId,
            skipDispatch: true,
            params: {
              page: 1,
              items: 100,
              tradingOfferTypes: [TradingOfferType.SELL],
              partner: partnerId,
              fields: 'sharesCount endDate type partner shareClass',
            },
          })
        );

        if (!active) return;

        if (!result?.data?.length) {
          setReservedSharesByShareClass(emptyMap);
          return;
        }

        const byClass = (result.data as TradingOffer[]).reduce<
          Record<string, number>
        >((acc, offer) => {
          if (excludeTradingOfferId && offer._id === excludeTradingOfferId) {
            return acc;
          }
          if (getOfferStatus(offer) !== 'ACTIVE') {
            return acc;
          }
          if (getPartnerIdFromOffer(offer.partner) !== partnerId) {
            return acc;
          }
          const classId = getShareClassId(offer.shareClass);
          if (!classId) {
            return acc;
          }
          acc[classId] = (acc[classId] || 0) + (offer.sharesCount?.total ?? 0);
          return acc;
        }, {});

        setReservedSharesByShareClass(byClass);
      } catch {
        if (active) {
          setReservedSharesByShareClass(emptyMap);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    run().catch(() => {
      /* errores ya contemplados en run */
    });

    return () => {
      active = false;
      setIsLoading(false);
    };
  }, [societyId, partnerId, excludeTradingOfferId, tradingOfferType]);

  return { reservedSharesByShareClass, isLoading };
};
